/**
 * useHandoffQueue Hook
 * Fetches and manages the handoff queue for the current organization.
 *
 * SPEC-2300: a handoff sits in `pending` for one 72-hour window and is claimed
 * or declined with a single pair of endpoints. There is no soft hold, no soft
 * expiry, and no 3-day extended window, so the old soft/hard action split — and
 * the "retry as hard-accept when the soft claim says expired" fallback — is gone.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '../../stores/useOrganizationStore';
import { useUser } from '../useUser';
import { useTeamMember } from '../useTeamMember';
import { HANDOFF_ACTION_SOURCE } from '../../utils/handoffTimeline';
import {
  getHandoffQueue,
  claimHandoff,
  declineHandoff,
  markHandoffConverted,
  cancelHandoff,
} from '../../api/handoff';
import {
  HandoffQueueItem,
  HandoffQueueResponse,
  ClaimHandoffResponse,
  DeclineHandoffResponse,
} from '../../types/handoff.types';

// Query keys
export const HANDOFF_QUERY_KEYS = {
  queue: (orgId: string) => ['handoff', 'queue', orgId] as const,
  stats: (orgId: string) => ['handoff', 'stats', orgId] as const,
  status: (orgId: string, contactId: string) => ['handoff', 'status', orgId, contactId] as const,
};

const NO_TEAM_MEMBER_ERROR =
  'You cannot claim this lead. Please add your account as a Team Member on your Team Roster.';

interface UseHandoffQueueOptions {
  enabled?: boolean;
  refetchInterval?: number | false;
  onNewHandoff?: (newHandoffs: HandoffQueueItem[]) => void;
}

interface UseHandoffQueueReturn {
  pendingHandoffs: HandoffQueueItem[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
  // Actions
  claim: (handoffId: string, notes?: string) => Promise<ClaimHandoffResponse>;
  decline: (handoffId: string, reason?: string, notes?: string) => Promise<DeclineHandoffResponse>;
  markConverted: (handoffId: string) => Promise<{ success: boolean; error?: string }>;
  cancel: (handoffId: string, reason?: string) => Promise<{ success: boolean; error?: string }>;
  isClaiming: boolean;
  isDeclining: boolean;
}

export const useHandoffQueue = (options: UseHandoffQueueOptions = {}): UseHandoffQueueReturn => {
  const { enabled = true, refetchInterval = 30000 } = options;
  const { organization } = useOrganizationStore();
  const { user } = useUser();
  const { teamMemberId } = useTeamMember();
  const queryClient = useQueryClient();

  const organizationId = organization?.id || '';
  // Use TeamMember ID for agentId (required by handoff-manager API), NOT Firebase UID
  const agentId = teamMemberId || '';

  const { data, isLoading, isError, error, refetch } = useQuery<HandoffQueueResponse, Error>({
    queryKey: HANDOFF_QUERY_KEYS.queue(organizationId),
    queryFn: () => getHandoffQueue(organizationId, agentId),
    enabled: enabled && !!organizationId && !!agentId,
    refetchInterval,
    staleTime: 10000, // Consider data stale after 10 seconds
  });

  // Claim and decline both take the row out of the pending queue, so the
  // optimistic update is the same drop for either action.
  const optimisticallyRemoveHandoff = (handoffId: string) => {
    queryClient.setQueryData<HandoffQueueResponse>(
      HANDOFF_QUERY_KEYS.queue(organizationId),
      (old) => {
        if (!old) return old;
        const filtered = old.handoffs?.filter((h) => h.handoffId !== handoffId) ?? [];
        return { ...old, handoffs: filtered, count: filtered.length };
      }
    );
  };

  // Notes are optional on claim: the endpoint persists them to `agent_notes`
  // (and onto the timeline event) when present, and an empty box must never
  // stand between an agent and a hot lead.
  const claimMutation = useMutation({
    mutationFn: async ({ handoffId, notes }: { handoffId: string; notes?: string }) => {
      if (!user) throw new Error('User not authenticated');
      if (!teamMemberId) throw new Error(NO_TEAM_MEMBER_ERROR);

      const handoff = data?.handoffs?.find((h) => h.handoffId === handoffId);
      return claimHandoff({
        organizationId,
        handoffId,
        contactId: handoff?.contactId,
        agentId: teamMemberId,
        agentName: user.displayName || `${user.firstName} ${user.lastName}` || user.email,
        source: HANDOFF_ACTION_SOURCE.inApp,
        ...(notes?.trim() ? { notes: notes.trim() } : {}),
      });
    },
    onSuccess: (result, { handoffId }) => {
      if (result.success) {
        optimisticallyRemoveHandoff(handoffId);
      }
      queryClient.invalidateQueries({ queryKey: HANDOFF_QUERY_KEYS.queue(organizationId) });
    },
  });

  // `reason` is the fixed machine label for the action; `notes` is whatever the
  // agent chose to type, and is optional exactly like it is on claim.
  const declineMutation = useMutation({
    mutationFn: async ({
      handoffId,
      reason,
      notes,
    }: {
      handoffId: string;
      reason?: string;
      notes?: string;
    }) => {
      if (!user) throw new Error('User not authenticated');
      if (!teamMemberId) throw new Error(NO_TEAM_MEMBER_ERROR);
      return declineHandoff({
        organizationId,
        handoffId,
        agentId: teamMemberId,
        reason,
        source: HANDOFF_ACTION_SOURCE.inApp,
        ...(notes?.trim() ? { notes: notes.trim() } : {}),
      });
    },
    onSuccess: (result, { handoffId }) => {
      if (result.success) {
        optimisticallyRemoveHandoff(handoffId);
      }
      queryClient.invalidateQueries({ queryKey: HANDOFF_QUERY_KEYS.queue(organizationId) });
    },
  });

  const convertMutation = useMutation({
    mutationFn: async (handoffId: string) => {
      if (!user) throw new Error('User not authenticated');
      if (!teamMemberId) throw new Error(NO_TEAM_MEMBER_ERROR);
      return markHandoffConverted(organizationId, handoffId, teamMemberId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HANDOFF_QUERY_KEYS.queue(organizationId) });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async ({ handoffId, reason }: { handoffId: string; reason?: string }) => {
      if (!user) throw new Error('User not authenticated');
      if (!teamMemberId) throw new Error(NO_TEAM_MEMBER_ERROR);
      return cancelHandoff(organizationId, handoffId, teamMemberId, reason);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HANDOFF_QUERY_KEYS.queue(organizationId) });
    },
  });

  return {
    pendingHandoffs: data?.handoffs || [],
    isLoading,
    isError,
    error: error || null,
    refetch,
    // Actions
    claim: (handoffId: string, notes?: string) =>
      claimMutation.mutateAsync({ handoffId, notes }),
    decline: (handoffId: string, reason?: string, notes?: string) =>
      declineMutation.mutateAsync({ handoffId, reason, notes }),
    markConverted: (handoffId: string) => convertMutation.mutateAsync(handoffId),
    cancel: (handoffId: string, reason?: string) =>
      cancelMutation.mutateAsync({ handoffId, reason }),
    isClaiming: claimMutation.isPending,
    isDeclining: declineMutation.isPending,
  };
};

export default useHandoffQueue;
